import { API_CONFIG } from './config';

// 通用响应类型
export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
}

// SECURITY: Token storage uses localStorage for simplicity.
// For production hardening, migrate to HttpOnly cookies (requires backend Set-Cookie support)
// and enforce strict CSP headers to reduce XSS surface.
export const getToken = (): string | null => localStorage.getItem('token');
export const setToken = (token: string): void => localStorage.setItem('token', token);
export const clearToken = (): void => localStorage.removeItem('token');
export const getRefreshToken = (): string | null => localStorage.getItem('refreshToken');
export const setRefreshToken = (token: string): void => localStorage.setItem('refreshToken', token);
export const clearRefreshToken = (): void => localStorage.removeItem('refreshToken');

// 用户信息管理
export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};
export const setUser = (user: Record<string, unknown>): void => localStorage.setItem('user', JSON.stringify(user));
export const clearUser = (): void => localStorage.removeItem('user');

// ========== 全局踢出事件 ==========
// 当检测到"账号已在其他设备登录"时，触发此事件通知 App 层弹窗
type KickOutReason = 'other_device' | 'session_expired';
type KickOutListener = (reason: KickOutReason) => void;
const kickOutListeners: Set<KickOutListener> = new Set();
let kickOutFired = false; // 防止重复触发

export function onKickOut(listener: KickOutListener): () => void {
  kickOutListeners.add(listener);
  return () => { kickOutListeners.delete(listener); };
}

function fireKickOut(reason: KickOutReason) {
  if (kickOutFired) return;
  kickOutFired = true;
  clearToken();
  clearRefreshToken();
  clearUser();
  kickOutListeners.forEach(fn => fn(reason));
}

export function resetKickOutFlag() {
  kickOutFired = false;
}

// ========== 心跳机制 ==========
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
const HEARTBEAT_INTERVAL = 45_000; // 45 秒

async function heartbeatCheck() {
  const token = getToken();
  if (!token) {
    stopHeartbeat();
    return;
  }
  try {
    const res = await fetch(`${API_CONFIG.BASE_URL}/user/profile`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      const data = await res.json().catch(() => ({ message: '' }));
      const msg = data.message || '';
      // 其他设备登录，直接踢出
      if (msg.includes('其他设备') || msg.includes('other device')) {
        fireKickOut('other_device');
        stopHeartbeat();
        return;
      }
      // Access Token 过期，尝试刷新
      if (getRefreshToken()) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = doRefreshToken().finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        if (newToken) {
          // 刷新成功，下次心跳会用新 token
          return;
        }
      }
      // 刷新也失败了，踢出
      fireKickOut('session_expired');
      stopHeartbeat();
    }
  } catch {
    // 网络错误忽略，下次心跳再试
  }
}

export function startHeartbeat() {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(heartbeatCheck, HEARTBEAT_INTERVAL);
}

export function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

// 是否正在刷新 token（防止并发刷新）
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

async function doRefreshToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/user/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    const data = await response.json();
    if (response.ok && data.code === 200 && data.data?.accessToken) {
      setToken(data.data.accessToken);
      return data.data.accessToken;
    }
    return null;
  } catch {
    return null;
  }
}

// 基础请求函数
export async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // 401 处理：区分"其他设备登录"和普通 token 过期
  if (response.status === 401) {
    // 先读取错误消息判断是否是踢出
    const errText = await response.text().catch(() => '');
    let errData = { message: '' };
    try { errData = errText ? JSON.parse(errText) : {}; } catch (e) { }
    const errMsg = errData.message || errText || '';

    // 如果是其他设备登录，直接触发踢出，不尝试刷新
    if (errMsg.includes('其他设备') || errMsg.includes('other device')) {
      fireKickOut('other_device');
      throw new Error(errMsg);
    }

    // 普通 token 过期，尝试 refreshToken 刷新
    if (getRefreshToken()) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = doRefreshToken().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        // 用新 token 重试原请求
        const retryHeaders: HeadersInit = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${newToken}`,
          ...options.headers,
        };
        const retryResponse = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
          ...options,
          headers: retryHeaders,
        });
        const retryData = await retryResponse.json();
        if (!retryResponse.ok || retryData.code !== 200) {
          throw new Error(retryData.message || '请求失败');
        }
        return retryData;
      }
    }

    // refreshToken 也失效了
    fireKickOut('session_expired');
    throw new Error('登录已过期，请重新登录');
  }

  const text = await response.text();
  let data: any = {}; // JSON.parse return type; validated before return
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = { code: response.status, message: text };
    }
  }

  if (!response.ok || (data.code && data.code !== 200)) {
    let resolvedMessage: string = data.message || data.msg || '请求失败';

    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      resolvedMessage = data.errors[0].defaultMessage || data.errors[0].msg || resolvedMessage;
    } else if (data.error && typeof data.error === 'string' && resolvedMessage === '请求失败') {
      resolvedMessage = data.error;
    }

    throw new Error(resolvedMessage);
  }

  return data;
}

// GET 请求
export function get<T>(endpoint: string): Promise<ApiResponse<T>> {
  return request<T>(endpoint, { method: 'GET' });
}

// POST 请求
export function post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
  return request<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

// PUT 请求
export function put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

// DELETE 请求
export function del<T>(endpoint: string): Promise<ApiResponse<T>> {
  return request<T>(endpoint, { method: 'DELETE' });
}

// 登出
export function logout(): void {
  clearToken();
  clearUser();
}
